import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { PaletteMode } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Layout from './components/Layout';
import ManagementLayout from './components/ManagementLayout';
import MockModeIndicator from './components/MockModeIndicator';
import InitialSetupModal from './components/InitialSetupModal';
import api from './services/api';
import { isMockEnabled } from './utils/mockMode';
import Dashboard from './pages/Dashboard';
import JobPostingTemplates from './pages/JobPostingTemplates';
import LineBroadcast from './pages/LineBroadcast';
import NotFound from './pages/NotFound';
import Applicants from './pages/Applicants';
import StoreSettings from './pages/StoreSettings';
import AccountSettings from './pages/AccountSettings';
import Login from './pages/login';
import StoreApplication from './pages/StoreApplication';
import PasswordReset from './pages/PasswordReset';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ManagementAuthProvider, useManagementAuth } from './auth/ManagementAuthContext';
import Home from './pages/Home';
import Info from './pages/Info';
import AnnouncementDetail from './pages/AnnouncementDetail';
import ManagementSettings from './pages/ManagementSettings';
import AttendanceQRPage from './pages/AttendanceQRPage';
import WorkSchedulePage from './pages/WorkSchedulePage';
import AttendanceApprovalPage from './pages/AttendanceApprovalPage';
import SkillSettings from './pages/SkillSettings';
import RankSettings from './pages/RankSettings';
import SpecialOfferTemplateSettings from './pages/SpecialOfferTemplateSettings';
import CrewManagement from './pages/CrewManagement';
import CrewBulkImportPage from './pages/CrewBulkImportPage';
import CrewDetail from './pages/CrewDetail';
import NotificationsPage from './pages/NotificationsPage';
import JobManagementPage from './pages/JobManagementPage';
import UserSettingsPage from './pages/UserSettingsPage';
import BillingPage from './pages/BillingPage';
import FeedbackPage from './pages/FeedbackPage';
import CompanyRegistration from './pages/CompanyRegistration';
import SelectStorePage from './pages/SelectStorePage';

// 管理アカウント用ページ
import CompanyStoreListPage from './pages/management/CompanyStoreListPage';
import CompanyInfoPage from './pages/management/CompanyInfoPage';
import CompanyStoresPage from './pages/management/CompanyStoresPage';
import ManagementBillingPage from './pages/management/BillingPage';
import BillingDetailPage from './pages/management/BillingDetailPage';
import WorkerRosterPage from './pages/management/WorkerRosterPage';
import ManagementAccountPage from './pages/management/ManagementAccountPage';
import EmailSettingsPage from './pages/management/EmailSettingsPage';
import StoreDetailPage from './pages/management/StoreDetailPage';
import ApprovalDashboard from './pages/management/ApprovalDashboard';
import SokyuConfigPage from './pages/SokyuConfigPage';
import CanonicalSkillsAdmin from './pages/CanonicalSkillsAdmin';

// テーマプロバイダーを含むプライベートルート用のコンポーネント（店舗ユーザー）
const ProtectedRoutes: React.FC = () => {
  const [mode, setMode] = useState<PaletteMode>('light');
  const { currentUser, loading, storeId } = useAuth();
  const [showInitialSetup, setShowInitialSetup] = useState(false);

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // 店舗名が未設定の場合に初期設定モーダルを表示
  useEffect(() => {
    if (!storeId) return;
    const checkInitialSetup = async () => {
      try {
        const storeDetail = await api.adminStore.getStoreDetail(storeId);
        if (!storeDetail.name?.trim()) {
          setShowInitialSetup(true);
        }
      } catch {
        // エラーの場合はモーダルを表示しない
      }
    };
    checkInitialSetup();
  }, [storeId]);

  const theme = createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'light' ? '#1976d2' : '#90caf9',
      },
      secondary: {
        main: mode === 'light' ? '#9c27b0' : '#ce93d8',
      },
      text: {
        primary: mode === 'light' ? '#898989' : '#e0e0e0',
        tertiary: '#3C3C3C',
      },
      background: {
        default: mode === 'light' ? '#f8f8f9' : '#121212',
      },
    },
    typography: {
      fontFamily: [
        '"Noto Sans JP"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif'
      ].join(','),
    },
    components: {
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? '#898989' : '#e0e0e0',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'light' ? '#f3f3f3' : '#2a2a2a',
            borderRadius: '12px',
            '& fieldset': {
              borderColor: mode === 'light' ? '#e7e7e7' : '#444',
            },
            '&:hover fieldset': {
              borderColor: mode === 'light' ? '#c8c8c8' : '#666',
            },
          },
        },
      },
    },
  });

  // 認証状態の読み込み中は読み込み中の表示
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // ユーザーがログインしていない場合はログインページにリダイレクト
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // 認証済みの場合はテーマプロバイダーとレイアウトを適用
  return (
    <ThemeProvider theme={theme}>
      <Layout mode={mode} toggleColorMode={toggleColorMode}>
        <Outlet />
      </Layout>
      <InitialSetupModal
        open={showInitialSetup}
        onComplete={() => setShowInitialSetup(false)}
        storeId={storeId || ''}
      />
    </ThemeProvider>
  );
};

// 管理アカウント用のプロテクトルート
// Re:Crew運営（isAdmin）または管理アカウント（isManagementUser）がアクセス可能
const ManagementProtectedRoutes: React.FC<{ mode: 'company-list' | 'company' | 'store' }> = ({ mode }) => {
  const { isAdmin } = useAuth();
  const { isManagementUser } = useManagementAuth();

  if (!isAdmin && !isManagementUser) {
    return <Navigate to="/login" replace />;
  }

  return <ManagementLayout mode={mode} />;
};

// 承認管理・運営設定・即給連携管理用のプロテクトルート
// サイドバーの表示条件（ManagementLayoutのisAdmin判定）と同じ条件をルートレベルでも要求する
const RequireAdminRoutes: React.FC<{ mode: 'company-list' }> = ({ mode }) => {
  const { isAdmin } = useAuth();
  const { isManagementUser } = useManagementAuth();

  if (!isAdmin && !isManagementUser) {
    return <Navigate to="/login" replace />;
  }
  if (!isAdmin) {
    return <Navigate to="/management" replace />;
  }

  return <ManagementLayout mode={mode} />;
};

// 正準スキル辞書用のプロテクトルート
// サイドバーの表示条件（ManagementLayoutのisSuperadmin判定）と同じ条件をルートレベルでも要求する
const RequireSuperadminRoutes: React.FC<{ mode: 'company-list' }> = ({ mode }) => {
  const { isAdmin } = useAuth();
  const { isManagementUser, isSuperadmin } = useManagementAuth();

  if (!isAdmin && !isManagementUser) {
    return <Navigate to="/login" replace />;
  }
  if (!isSuperadmin) {
    return <Navigate to="/management" replace />;
  }

  return <ManagementLayout mode={mode} />;
};

const App: React.FC = () => {
  // ログインページ用の基本テーマ
  const baseTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#9c27b0',
      },
      text: {
        primary: '#898989',
      },
      background: {
        default: '#f8f8f9',
      },
    },
    typography: {
      fontFamily: [
        '"Noto Sans JP"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif'
      ].join(','),
    },
    components: {
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: '#898989',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: '#f3f3f3',
            borderRadius: '12px',
            '& fieldset': {
              borderColor: '#e7e7e7',
            },
            '&:hover fieldset': {
              borderColor: '#c8c8c8',
            },
          },
        },
      },
    },
  });

  return (
    <AuthProvider>
      <ManagementAuthProvider>
        <ThemeProvider theme={baseTheme}>
          <CssBaseline />
          <MockModeIndicator />
          <Box sx={{ pt: isMockEnabled() ? '36px' : 0 }}>
          <Router>
            <Routes>
              {/* ログインページ - 認証なしでアクセス可能 */}
              <Route path="/login" element={<Login />} />
              {/* パスワードリセットページ - 認証なしでアクセス可能 */}
              <Route path="/password-reset" element={<PasswordReset />} />
              {/* 店舗新規申込ページ - 認証なしでアクセス可能 */}
              <Route path="/store-application" element={<StoreApplication />} />
              {/* 店舗選択ページ - ログイン後に複数店舗がある場合 */}
              <Route path="/select-store" element={<SelectStorePage />} />

              {/* ========== 管理アカウント用ルート ========== */}

              {/* 企業/店舗一覧 */}
              <Route element={<ManagementProtectedRoutes mode="company-list" />}>
                <Route path="/management" element={<CompanyStoreListPage />} />
              </Route>

              {/* 運営設定（承認管理・運営設定・即給連携管理） */}
              <Route element={<RequireAdminRoutes mode="company-list" />}>
                <Route path="/management/settings" element={<ManagementSettings />} />
                <Route path="/management/approvals" element={<ApprovalDashboard />} />
                <Route path="/admin/sokyu-config" element={<SokyuConfigPage />} />
              </Route>

              {/* 正準スキル辞書（Re:Crew運営者専用） */}
              <Route element={<RequireSuperadminRoutes mode="company-list" />}>
                <Route path="/admin/canonical-skills" element={<CanonicalSkillsAdmin />} />
              </Route>

              {/* 企業管理画面 */}
              <Route element={<ManagementProtectedRoutes mode="company" />}>
                <Route path="/management/company/:companyId" element={<CompanyInfoPage />} />
                <Route path="/management/company/:companyId/stores" element={<CompanyStoresPage />} />
                <Route path="/management/company/:companyId/billing" element={<ManagementBillingPage />} />
                <Route path="/management/company/:companyId/billing/:recordId" element={<BillingDetailPage />} />
                <Route path="/management/company/:companyId/workers" element={<WorkerRosterPage />} />
                <Route path="/management/company/:companyId/email-settings" element={<EmailSettingsPage />} />
                <Route path="/management/company/:companyId/account" element={<ManagementAccountPage />} />
                <Route path="/management/company/:companyId/store/:storeId" element={<StoreDetailPage />} />
              </Route>

              {/* ========== 店舗ユーザー用ルート ========== */}
              <Route element={<ProtectedRoutes />}>
                <Route path="/" element={<Home />} />
                <Route path="/Info" element={<Info />} />
                <Route path="/announcements/:id" element={<AnnouncementDetail />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/job-templates" element={<JobPostingTemplates />} />
                <Route path="/applicants" element={<Applicants />} />
                <Route path="/line-broadcast" element={<LineBroadcast />} />
                <Route path="/settings/store" element={<StoreSettings />} />
                <Route path="/settings/account" element={<AccountSettings />} />
                <Route path="/settings/user" element={<UserSettingsPage />} />
                <Route path="/attendance/qr" element={<AttendanceQRPage />} />
                <Route path="/attendance/schedule" element={<WorkSchedulePage />} />
                <Route path="/attendance/approval" element={<AttendanceApprovalPage />} />
                <Route path="/skill-settings" element={<SkillSettings />} />
                <Route path="/rank-settings" element={<RankSettings />} />
                <Route path="/special-offer-templates" element={<SpecialOfferTemplateSettings />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/job-management" element={<JobManagementPage />} />
                <Route path="/crew-management" element={<CrewManagement />} />
                <Route path="/crew-management/import" element={<CrewBulkImportPage />} />
                <Route path="/crew-management/:storeUserId" element={<CrewDetail />} />
                <Route path="/billing" element={<BillingPage />} />
                <Route path="/feedback" element={<FeedbackPage />} />
                <Route path="/company-registration" element={<CompanyRegistration />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Router>
          </Box>
        </ThemeProvider>
      </ManagementAuthProvider>
    </AuthProvider>
  );
};

export default App;
